// src/pages/NotasPagamento.jsx
import React, {useEffect, useState} from 'react';
import {
    Box,
    Button,
    Divider,
    Paper,
    Typography,
    TableContainer,
    Table,
    TableHead,
    TableRow,
    TableCell,
    TableBody,
    IconButton,
    TextField,
    Box as MuiBox,
} from '@mui/material';
import {Title} from '../../components/ui/Title';
import './../../styles/Fornecedor.css';
import {fetchNotasRetorno} from '../../services/easyhubQueries';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import LoadingScreen from '../../components/common/loading';
import {anexarArquivosNfOs} from '../../services/easyhubQueries';
import SearchIcon from '@mui/icons-material/Search';
import {format, subMonths} from 'date-fns';

export default function NotasPagamento() {
    const [notas, setNotas] = useState([]);
    const [isLoading, setIsLoading] = useState(true);

    // helper bem simples
    const isEmpty = (v) => String(v ?? '').trim() === '';

    // datas: padrão últimos 3 meses até hoje (mesmo das Solicitações)
    const hoje = new Date();
    const defaultEnd = format(hoje, 'yyyy-MM-dd');
    const defaultStart = format(subMonths(hoje, 3), 'yyyy-MM-dd');
    const [startDate, setStartDate] = useState(defaultStart);
    const [endDate, setEndDate] = useState(defaultEnd);

    useEffect(() => {
        async function carregarNotas() {
            setIsLoading(true);
            try {
                // "yyyy-MM-dd" -> "yyyyMMdd"
                const ini = (startDate || '').replace(/-/g, '');
                const fim = (endDate || '').replace(/-/g, '');
                const res = await fetchNotasRetorno(ini, fim);
                console.log('Notas de Pagamento:', res);
                if (res?.Retorno === 'OK' && Array.isArray(res.Dados)) {
                    const formatado = res.Dados.map((item, idx) => ({
                        id: idx,
                        ordem: item.OS,
                        pedidoCompra: item.PED_COMPRA,
                        nota: item.NOTA,
                        emitidoEm: formatarData(item.EMISS_NOTA),
                        parcela: item.PARCELA || '-',
                        valor: `R$ ${item.VLR_TOTAL?.toFixed(2) || '0,00'}`,
                        previsaoPagamento: formatarData(item.PREV_PAGTO),
                        pagoEm: item.PAGO_EM ? formatarData(item.PAGO_EM) : '-',
                    }));
                    setNotas(formatado);
                }
            } catch (err) {
                console.error('Erro ao buscar notas:', err);
            } finally {
                setIsLoading(false);
            }
        }

        carregarNotas();
    }, []);

    const columnTitles = [
        'Ordem',
        'Pedido Compra',
        'Nota',
        'Emitido em',
        'Parcela',
        'Valor',
        'Previsão pago',
        'Pago em',
        'Anexar nota',
    ];

    function formatarData(yyyymmdd) {
        if (!yyyymmdd || yyyymmdd.length !== 8) return '-';
        return `${yyyymmdd.slice(6, 8)}/${yyyymmdd.slice(4, 6)}/${yyyymmdd.slice(0, 4)}`;
    }
    if (isLoading) return <LoadingScreen message="Carregando notas de pagamento..." />;
    return (
        <Paper elevation={1} sx={{p: 3, mb: 3, borderRadius: 3}}>
            <Title>Notas de Pagamento</Title>
            <Divider sx={{my: 2}} />
            {/* Filtro por período - igual às Solicitações */}
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'flex-end',
                    gap: 1,
                    mb: 2,
                }}
            >
                <TextField
                    label="Data início"
                    type="date"
                    size="small"
                    value={startDate}
                    onChange={(e) => setStartDate(e.target.value)}
                    InputLabelProps={{shrink: true}}
                />
                <TextField
                    label="Data fim"
                    type="date"
                    size="small"
                    value={endDate}
                    onChange={(e) => setEndDate(e.target.value)}
                    InputLabelProps={{shrink: true}}
                />
                <IconButton
                    color="primary"
                    onClick={async () => {
                        setIsLoading(true);
                        try {
                            const ini = (startDate || '').replace(/-/g, '');
                            const fim = (endDate || '').replace(/-/g, '');
                            const res = await fetchNotasRetorno(ini, fim);
                            if (res?.Retorno === 'OK' && Array.isArray(res.Dados)) {
                                const formatado = res.Dados.map((item, idx) => ({
                                    id: idx,
                                    ordem: item.OS,
                                    pedidoCompra: item.PED_COMPRA,
                                    nota: item.NOTA,
                                    emitidoEm: formatarData(item.EMISS_NOTA),
                                    parcela: item.PARCELA || '-',
                                    valor: `R$ ${item.VLR_TOTAL?.toFixed(2) || '0,00'}`,
                                    previsaoPagamento: formatarData(item.PREV_PAGTO),
                                    pagoEm: item.PAGO_EM ? formatarData(item.PAGO_EM) : '-',
                                }));
                                setNotas(formatado);
                            } else {
                                setNotas([]);
                            }
                        } catch (err) {
                            console.error('Erro ao buscar notas:', err);
                            setNotas([]);
                        } finally {
                            setIsLoading(false);
                        }
                    }}
                    aria-label="Buscar por período"
                >
                    <SearchIcon />
                </IconButton>
            </Box>
            <TableContainer component={Box} sx={{border: '1px solid #ccc'}}>
                <Table>
                    <TableHead sx={{bgcolor: '#f9fafb'}}>
                        <TableRow>
                            {columnTitles.map((title, idx) => (
                                <TableCell key={idx} align="center" sx={{fontWeight: 'bold'}}>
                                    {title}
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>

                    <TableBody>
                        {notas.length > 0 ? (
                            notas.map((nota, index) => (
                                <TableRow key={index} hover>
                                    <TableCell align="center">{nota.ordem}</TableCell>
                                    <TableCell align="center">{nota.pedidoCompra}</TableCell>
                                    <TableCell align="center">{nota.nota}</TableCell>
                                    <TableCell align="center">{nota.emitidoEm}</TableCell>
                                    <TableCell align="center">{nota.parcela}</TableCell>
                                    <TableCell align="center">{nota.valor}</TableCell>
                                    <TableCell align="center">{nota.previsaoPagamento}</TableCell>
                                    <TableCell align="center">{nota.pagoEm}</TableCell>
                                    <TableCell align="center">
                                        <input
                                            type="file"
                                            style={{display: 'none'}}
                                            id={`attach-input-${nota.id}`}
                                            accept=".pdf,.jpg,.jpeg,.png"
                                            onChange={async (e) => {
                                                const file = e.target.files[0];
                                                if (!file) return;
                                                if (isEmpty(nota.pedidoCompra)) {
                                                    alert(
                                                        'Pedido de compra vazio. Preencha para anexar a nota.',
                                                    );
                                                    e.target.value = ''; // limpa a seleção
                                                    return;
                                                }
                                                try {
                                                    const reader = new FileReader();
                                                    reader.onloadend = async () => {
                                                        const base64 = reader.result.split(',')[1];
                                                        const anexo = {
                                                            name: file.name,
                                                            Img: base64,
                                                        };
                                                        const resp = await anexarArquivosNfOs(
                                                            nota.pedidoCompra,
                                                            [anexo],
                                                        );
                                                        if (resp?.Retorno === 'OK') {
                                                            alert('Anexo enviado com sucesso!');
                                                        } else {
                                                            alert(
                                                                'Erro ao enviar anexo: ' +
                                                                    resp?.Retorno,
                                                            );
                                                        }
                                                    };
                                                    reader.readAsDataURL(file);
                                                } catch (err) {
                                                    console.error('Erro ao anexar nota:', err);
                                                    alert('Falha ao anexar arquivo.');
                                                }
                                            }}
                                        />
                                        <label htmlFor={`attach-input-${nota.id}`}>
                                            <IconButton component="span">
                                                <AttachFileIcon />
                                            </IconButton>
                                        </label>
                                    </TableCell>
                                </TableRow>
                            ))
                        ) : (
                            <TableRow>
                                <TableCell
                                    colSpan={9}
                                    align="center"
                                    sx={{py: 3, color: '#9CA3AF'}}
                                >
                                    Nenhuma nota cadastrada.
                                </TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );
}
